import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { devLog } from '../utils/devLogger';

export interface EvidenceConfig {
  defaultEvidenceTarget: number;
  categoryTargets: Record<string, number>;
  criterionTargets: Record<string, number>;
  statusThresholds?: {
    strong: { min_cards: number; recent_days: number };
    adequate: { min_cards: number; recent_days: number };
    weak: { min_cards: number; stale_after_days: number };
  };
  timelineMilestones?: {
    schedule_meeting: number;
    final_week: number;
    confirm_meeting: number;
  };
  reviewChecklist?: any[];
  durationConfig?: {
    expected_duration_weeks: number | null;
    auto_extend: boolean;
    extension_buffer_weeks: number;
  };
  phaseConfig?: {
    phases: Array<{
      name: string;
      percentage: number;
      min_weeks: number;
    }>;
  };
  visualizationThresholds?: {
    coverage_colors: {
      high: { threshold: number; color: string };
      medium: { threshold: number; color: string };
      low: { threshold: number; color: string };
    };
  };
  displayLimits?: {
    recommendations_count: number;
    featured_achievements_max: number;
    [key: string]: number; // Allow for future display limits
  };
}

/**
 * Hook to load evidence target configuration for a focus
 * Merges configuration from multiple levels:
 * 1. Focus-level config (focus_evidence_config table)
 * 2. Category-level defaults (rubric_categories.default_evidence_target)
 * 3. Criterion-level overrides (rubric_criteria.evidence_target)
 */
export const useEvidenceConfig = (focusId: string, rubricId?: string) => {
  const [config, setConfig] = useState<EvidenceConfig>({
    defaultEvidenceTarget: 3,
    categoryTargets: {},
    criterionTargets: {},
    statusThresholds: {
      strong: { min_cards: 3, recent_days: 7 },
      adequate: { min_cards: 2, recent_days: 14 },
      weak: { min_cards: 1, stale_after_days: 14 }
    },
    timelineMilestones: {
      schedule_meeting: 14,
      final_week: 7,
      confirm_meeting: 3
    },
    displayLimits: {
      recommendations_count: 3,
      featured_achievements_max: 5
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!focusId) {
      setLoading(false);
      return;
    }

    const loadConfig = async () => {
      try {
        // 1. Load focus-level config
        const { data: focusConfig } = await supabase
          .from('focus_evidence_config')
          .select('*')
          .eq('focus_id', focusId)
          .single();

        let defaultTarget = focusConfig?.default_evidence_target || 3;
        const statusThresholds = focusConfig?.status_thresholds || {
          strong: { min_cards: 3, recent_days: 7 },
          adequate: { min_cards: 2, recent_days: 14 },
          weak: { min_cards: 1, stale_after_days: 14 }
        };
        const timelineMilestones = focusConfig?.timeline_milestones || {
          schedule_meeting: 14,
          final_week: 7,
          confirm_meeting: 3
        };
        const reviewChecklist = focusConfig?.review_checklist;
        const durationConfig = focusConfig?.duration_config || {
          expected_duration_weeks: null,
          auto_extend: true,
          extension_buffer_weeks: 4
        };
        const phaseConfig = focusConfig?.phase_config || {
          phases: [
            { name: 'onboarding', percentage: 0.08, min_weeks: 1 },
            { name: 'foundation', percentage: 0.17, min_weeks: 2 },
            { name: 'growth', percentage: 0.25, min_weeks: 3 },
            { name: 'impact', percentage: 0.35, min_weeks: 4 },
            { name: 'transition', percentage: 0.15, min_weeks: 2 }
          ]
        };
        const visualizationThresholds = focusConfig?.visualization_thresholds || {
          coverage_colors: {
            high: { threshold: 60, color: 'green' },
            medium: { threshold: 30, color: 'yellow' },
            low: { threshold: 0, color: 'red' }
          }
        };
        const displayLimits = focusConfig?.display_limits || {
          recommendations_count: 3,
          featured_achievements_max: 5
        };
        const categoryTargets: Record<string, number> = {};
        const criterionTargets: Record<string, number> = {};

        // 2. If we have a rubric, load category and criterion configs
        if (rubricId) {
          // Load categories with their default targets
          const { data: categories } = await supabase
            .from('rubric_categories')
            .select('id, name, default_evidence_target')
            .eq('rubric_id', rubricId);

          if (categories) {
            categories.forEach(cat => {
              if (cat.default_evidence_target) {
                categoryTargets[cat.name] = cat.default_evidence_target;
              }
            });

            // Load criteria with their specific targets
            const categoryIds = categories.map(c => c.id);
            const { data: criteria } = await supabase
              .from('rubric_criteria')
              .select('id, category_id, evidence_target')
              .in('category_id', categoryIds);

            if (criteria) {
              criteria.forEach(criterion => {
                if (criterion.evidence_target) {
                  criterionTargets[criterion.id] = criterion.evidence_target;
                }
              });
            }
          }
        }

        // Additional config from JSONB if needed
        const additionalConfig = focusConfig?.config || {};

        setConfig({
          defaultEvidenceTarget: defaultTarget,
          categoryTargets: {
            ...categoryTargets,
            ...(additionalConfig.categoryTargets || {})
          },
          criterionTargets: {
            ...criterionTargets,
            ...(additionalConfig.criterionTargets || {})
          },
          statusThresholds,
          timelineMilestones,
          reviewChecklist,
          durationConfig,
          phaseConfig,
          visualizationThresholds,
          displayLimits
        });
      } catch (error) {
        devLog.error('Error loading evidence config:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [focusId, rubricId]);

  return { config, loading };
};

/**
 * Hook to save evidence configuration
 */
export const useSaveEvidenceConfig = () => {
  const saveConfig = async (
    focusId: string, 
    defaultTarget: number,
    additionalConfig?: any
  ) => {
    try {
      const { error } = await supabase
        .from('focus_evidence_config')
        .upsert({
          focus_id: focusId,
          default_evidence_target: defaultTarget,
          config: additionalConfig || {},
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'focus_id'
        });

      if (error) throw error;
      return true;
    } catch (error) {
      devLog.error('Error saving evidence config:', error);
      return false;
    }
  };

  const updateStatusThresholds = async (
    focusId: string,
    thresholds: {
      strong: { min_cards: number; recent_days: number };
      adequate: { min_cards: number; recent_days: number };
      weak: { min_cards: number; stale_after_days: number };
    }
  ) => {
    try {
      const { error } = await supabase
        .from('focus_evidence_config')
        .upsert({
          focus_id: focusId,
          status_thresholds: thresholds,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'focus_id'
        });

      if (error) throw error;
      return true;
    } catch (error) {
      devLog.error('Error updating status thresholds:', error);
      return false;
    }
  };

  const updateTimelineMilestones = async (
    focusId: string,
    milestones: {
      schedule_meeting: number;
      final_week: number;
      confirm_meeting: number;
    }
  ) => {
    try {
      const { error } = await supabase
        .from('focus_evidence_config')
        .upsert({
          focus_id: focusId,
          timeline_milestones: milestones,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'focus_id'
        });

      if (error) throw error;
      return true;
    } catch (error) {
      devLog.error('Error updating timeline milestones:', error);
      return false;
    }
  };

  const updateDurationConfig = async (
    focusId: string,
    durationConfig: {
      expected_duration_weeks: number | null;
      auto_extend: boolean;
      extension_buffer_weeks: number;
    }
  ) => {
    try {
      const { error } = await supabase
        .from('focus_evidence_config')
        .upsert({
          focus_id: focusId,
          duration_config: durationConfig,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'focus_id'
        });

      if (error) throw error;
      return true;
    } catch (error) {
      devLog.error('Error updating duration config:', error);
      return false;
    }
  };

  const updatePhaseConfig = async (
    focusId: string,
    phaseConfig: {
      phases: Array<{
        name: string;
        percentage: number;
        min_weeks: number;
      }>;
    }
  ) => {
    try {
      const { error } = await supabase
        .from('focus_evidence_config')
        .upsert({
          focus_id: focusId,
          phase_config: phaseConfig,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'focus_id'
        });

      if (error) throw error;
      return true;
    } catch (error) {
      devLog.error('Error updating phase config:', error);
      return false;
    }
  };

  const updateCategoryTarget = async (
    categoryId: string,
    target: number
  ) => {
    try {
      const { error } = await supabase
        .from('rubric_categories')
        .update({ default_evidence_target: target })
        .eq('id', categoryId);

      if (error) throw error;
      return true;
    } catch (error) {
      devLog.error('Error updating category target:', error);
      return false;
    }
  };

  const updateCriterionTarget = async (
    criterionId: string,
    target: number
  ) => {
    try {
      const { error } = await supabase
        .from('rubric_criteria')
        .update({ evidence_target: target })
        .eq('id', criterionId);

      if (error) throw error;
      return true;
    } catch (error) {
      devLog.error('Error updating criterion target:', error);
      return false;
    }
  };

  const updateDisplayLimits = async (
    focusId: string,
    displayLimits: {
      recommendations_count?: number;
      featured_achievements_max?: number;
      [key: string]: number | undefined;
    }
  ) => {
    try {
      const { error } = await supabase
        .from('focus_evidence_config')
        .upsert({
          focus_id: focusId,
          display_limits: displayLimits,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'focus_id'
        });

      if (error) throw error;
      return true;
    } catch (error) {
      devLog.error('Error updating display limits:', error);
      return false;
    }
  };

  return {
    saveConfig,
    updateCategoryTarget,
    updateCriterionTarget,
    updateStatusThresholds,
    updateTimelineMilestones,
    updateDurationConfig,
    updatePhaseConfig,
    updateDisplayLimits
  };
};